import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthGuard } from '@/src/components/auth-guard';
import DashboardLayout from '@/app/(dashboard)/layout';
import InboxLayout from '@/app/(dashboard)/social-manager/inbox/layout';
import ProductFormLayout from '@/app/(dashboard)/e-commerce/products/[id]/layout';
import { ECommerceLayoutWrapper } from '@/src/wrappers/e-commerce-layout';
import { CustomizationLayoutWrapper } from '@/src/wrappers/customization-layout';
import { SettingsLayoutWrapper } from '@/src/wrappers/settings-layout';
import { AnalyticsLayoutWrapper } from '@/src/wrappers/analytics-layout';

const LoginPage = lazy(() => import('@/app/login/page'));
const ForgotPasswordPage = lazy(() => import('@/app/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('@/app/reset-password/page'));
const RegisterPage = lazy(() => import('@/app/register/page'));
const AcceptInvitePage = lazy(() => import('@/app/accept-invite/page'));
const DashboardHomePage = lazy(() => import('@/app/(dashboard)/dashboard/page'));

const ProductsPage = lazy(() => import('@/app/(dashboard)/e-commerce/products/page'));
const ProductPage = lazy(() => import('@/app/(dashboard)/e-commerce/products/[id]/page'));
const ProductDetailsPage = lazy(
  () => import('@/app/(dashboard)/e-commerce/products/[id]/details/page')
);
const ProductMediaPage = lazy(
  () => import('@/app/(dashboard)/e-commerce/products/[id]/media/page')
);
const ProductVariantsPage = lazy(
  () => import('@/app/(dashboard)/e-commerce/products/[id]/variants-inventory/page')
);
const ProductGroupsPage = lazy(() => import('@/app/(dashboard)/e-commerce/product-groups/page'));
const ProductGroupPage = lazy(
  () => import('@/app/(dashboard)/e-commerce/product-groups/[id]/page')
);
const ProductGroupNewPage = lazy(
  () => import('@/app/(dashboard)/e-commerce/product-groups/new/page')
);
const ProductTemplatesPage = lazy(
  () => import('@/app/(dashboard)/e-commerce/product-templates/page')
);
const ProductTemplatePage = lazy(
  () => import('@/app/(dashboard)/e-commerce/product-templates/[id]/page')
);
const OrdersPage = lazy(() => import('@/app/(dashboard)/e-commerce/orders/page'));
const OrderNewPage = lazy(() => import('@/app/(dashboard)/e-commerce/orders/new/page'));
const OrderPage = lazy(() => import('@/app/(dashboard)/e-commerce/orders/[id]/page'));
const OrderEditPage = lazy(() => import('@/app/(dashboard)/e-commerce/orders/[id]/edit/page'));
const ShipmentsPage = lazy(() => import('@/app/(dashboard)/e-commerce/shipments/page'));
const BrandsPage = lazy(() => import('@/app/(dashboard)/e-commerce/brands/page'));
const BrandPage = lazy(() => import('@/app/(dashboard)/e-commerce/brands/[id]/page'));
const CategoriesPage = lazy(() => import('@/app/(dashboard)/e-commerce/categories/page'));
const CategoryPage = lazy(() => import('@/app/(dashboard)/e-commerce/categories/[id]/page'));
const TagsPage = lazy(() => import('@/app/(dashboard)/e-commerce/tags/page'));
const TagPage = lazy(() => import('@/app/(dashboard)/e-commerce/tags/[id]/page'));
const CouponsPage = lazy(() => import('@/app/(dashboard)/e-commerce/coupons/page'));
const CouponNewPage = lazy(() => import('@/app/(dashboard)/e-commerce/coupons/new/page'));
const CouponEditPage = lazy(() => import('@/app/(dashboard)/e-commerce/coupons/[id]/edit/page'));
const PostsPage = lazy(() => import('@/app/(dashboard)/e-commerce/posts/page'));

const InboxPage = lazy(() => import('@/app/(dashboard)/social-manager/inbox/page'));
const InboxArchivedPage = lazy(
  () => import('@/app/(dashboard)/social-manager/inbox/archived/page')
);
const ChannelsPage = lazy(() => import('@/app/(dashboard)/social-manager/channels/page'));
const ConnectChannelPage = lazy(
  () => import('@/app/(dashboard)/social-manager/channels/connect/page')
);
const FacebookPagesPage = lazy(
  () => import('@/app/(dashboard)/social-manager/channels/facebook/pages/page')
);
const WhatsAppPage = lazy(() => import('@/app/(dashboard)/social-manager/channels/whatsapp/page'));
const PublisherPage = lazy(() => import('@/app/(dashboard)/social-manager/publisher/page'));
const PublisherMainPage = lazy(
  () => import('@/app/(dashboard)/social-manager/publisher/publisher/page')
);
const PostGroupsPage = lazy(
  () => import('@/app/(dashboard)/social-manager/publisher/post-groups/page')
);
const PostGroupPage = lazy(
  () => import('@/app/(dashboard)/social-manager/publisher/post-groups/[id]/page')
);
const PublisherCalendarPage = lazy(
  () => import('@/app/(dashboard)/social-manager/publisher/calendar/page')
);
const ChannelSelectorPage = lazy(
  () => import('@/app/(dashboard)/social-manager/publisher/channel-selector/page')
);
const AdsPage = lazy(() => import('@/app/(dashboard)/social-manager/ads/page'));
const AdsCreatePage = lazy(() => import('@/app/(dashboard)/social-manager/ads/create/page'));

const CustomizationPage = lazy(() => import('@/app/(dashboard)/customization/page'));
const ThemesPage = lazy(() => import('@/app/(dashboard)/customization/themes/page'));
const PresetsPage = lazy(() => import('@/app/(dashboard)/customization/presets/page'));
const AppliedPresetsPage = lazy(
  () => import('@/app/(dashboard)/customization/presets/applied/page')
);

const SettingsGeneralPage = lazy(() => import('@/app/(dashboard)/settings/general/page'));
const SettingsEcommercePage = lazy(() => import('@/app/(dashboard)/settings/ecommerce/page'));
const SettingsPaymentShippingPage = lazy(
  () => import('@/app/(dashboard)/settings/payment-shipping/page')
);
const SettingsCustomersPage = lazy(() => import('@/app/(dashboard)/settings/customers/page'));
const SettingsUsersPage = lazy(() => import('@/app/(dashboard)/settings/users/page'));
const SettingsIntegrationsPage = lazy(() => import('@/app/(dashboard)/settings/integrations/page'));
const SettingsDeliveryProvidersPage = lazy(
  () => import('@/app/(dashboard)/settings/delivery-providers/page')
);
const SettingsNotificationsPage = lazy(
  () => import('@/app/(dashboard)/settings/notifications/page')
);

const SettingsFeaturesPage = lazy(() => import('@/app/(dashboard)/settings/features/page'));

const AnalyticsPage = lazy(() => import('@/app/(dashboard)/analytics/page'));
const AnalyticsEcommercePage = lazy(() => import('@/app/(dashboard)/analytics/ecommerce/page'));
const AnalyticsSocialPage = lazy(() => import('@/app/(dashboard)/analytics/social/page'));
const AnalyticsDeliveryPage = lazy(() => import('@/app/(dashboard)/analytics/delivery/page'));

const EmailTemplatesPage = lazy(() => import('@/app/(dashboard)/automation/email-templates/page'));
const EmailTemplatePage = lazy(
  () => import('@/app/(dashboard)/automation/email-templates/[id]/page')
);
const EmailTemplateNewPage = lazy(
  () => import('@/app/(dashboard)/automation/email-templates/new/page')
);
const WorkflowsPage = lazy(() => import('@/app/(dashboard)/automation/workflows/page'));
const WorkflowPage = lazy(() => import('@/app/(dashboard)/automation/workflows/[id]/page'));
const WorkflowNewPage = lazy(() => import('@/app/(dashboard)/automation/workflows/new/page'));
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const WorkflowNavigate = () => {
  const { id } = useParams();
  return <Navigate to={`/settings/workflows/${id}`} replace />;
};

const EmailTemplateNavigate = () => {
  const { id } = useParams();
  return <Navigate to={`/settings/email-templates/${id}`} replace />;
};

export function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        <Route
          path="/"
          element={
            <AuthGuard>
              <DashboardLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardHomePage />} />

          <Route path="e-commerce" element={<ECommerceLayoutWrapper />}>
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/new" element={<ProductPage />} />
            <Route path="products/:id" element={<ProductFormLayout />}>
              <Route index element={<ProductPage />} />
              <Route path="details" element={<ProductDetailsPage />} />
              <Route path="media" element={<ProductMediaPage />} />
              <Route path="variants-inventory" element={<ProductVariantsPage />} />
            </Route>
            <Route path="product-groups" element={<ProductGroupsPage />} />
            <Route path="product-groups/new" element={<ProductGroupNewPage />} />
            <Route path="product-groups/:id" element={<ProductGroupPage />} />
            <Route path="product-templates" element={<ProductTemplatesPage />} />
            <Route path="product-templates/:id" element={<ProductTemplatePage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/new" element={<OrderNewPage />} />
            <Route path="orders/:id" element={<OrderPage />} />
            <Route path="orders/:id/edit" element={<OrderEditPage />} />
            <Route path="shipments" element={<ShipmentsPage />} />
            <Route path="brands" element={<BrandsPage />} />
            <Route path="brands/:id" element={<BrandPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="categories/:id" element={<CategoryPage />} />
            <Route path="tags" element={<TagsPage />} />
            <Route path="tags/:id" element={<TagPage />} />
            <Route path="coupons" element={<CouponsPage />} />
            <Route path="coupons/new" element={<CouponNewPage />} />
            <Route path="coupons/:id/edit" element={<CouponEditPage />} />
            <Route path="posts" element={<PostsPage />} />
          </Route>

          <Route path="social-manager" element={<Outlet />}>
            <Route
              path="inbox"
              element={
                <InboxLayout>
                  <InboxPage />
                </InboxLayout>
              }
            />
            <Route
              path="inbox/archived"
              element={
                <InboxLayout>
                  <InboxArchivedPage />
                </InboxLayout>
              }
            />
            <Route path="channels" element={<Navigate to="/settings/channels" replace />} />
            <Route path="channels/connect" element={<Navigate to="/settings/channels/connect" replace />} />
            <Route path="channels/facebook/pages" element={<Navigate to="/settings/channels/facebook/pages" replace />} />
            <Route path="channels/whatsapp" element={<Navigate to="/settings/channels/whatsapp" replace />} />
            <Route path="publisher" element={<PublisherPage />} />
            <Route path="publisher/publisher" element={<PublisherMainPage />} />
            <Route path="publisher/post-groups" element={<PostGroupsPage />} />
            <Route path="publisher/post-groups/:id" element={<PostGroupPage />} />
            <Route path="publisher/calendar" element={<PublisherCalendarPage />} />
            <Route path="publisher/channel-selector" element={<ChannelSelectorPage />} />
            <Route path="ads" element={<AdsPage />} />
            <Route path="ads/create" element={<AdsCreatePage />} />
          </Route>

          <Route path="customization" element={<CustomizationLayoutWrapper />}>
            <Route index element={<CustomizationPage />} />
            <Route path="themes" element={<ThemesPage />} />
            <Route path="presets" element={<PresetsPage />} />
            <Route path="presets/applied" element={<AppliedPresetsPage />} />
          </Route>

          <Route path="settings" element={<SettingsLayoutWrapper />}>
            <Route path="general" element={<SettingsGeneralPage />} />
            <Route path="channels" element={<ChannelsPage />} />
            <Route path="channels/connect" element={<ConnectChannelPage />} />
            <Route path="channels/facebook/pages" element={<FacebookPagesPage />} />
            <Route path="channels/whatsapp" element={<WhatsAppPage />} />
            <Route path="ecommerce" element={<SettingsEcommercePage />} />
            <Route path="payment-shipping" element={<SettingsPaymentShippingPage />} />
            <Route path="customers" element={<SettingsCustomersPage />} />
            <Route path="users" element={<SettingsUsersPage />} />
            <Route path="integrations" element={<SettingsIntegrationsPage />} />
            <Route path="delivery-providers" element={<SettingsDeliveryProvidersPage />} />
            <Route path="notifications" element={<SettingsNotificationsPage />} />
            <Route path="features" element={<SettingsFeaturesPage />} />
            <Route path="workflows" element={<WorkflowsPage />} />
            <Route path="workflows/new" element={<WorkflowNewPage />} />
            <Route path="workflows/:id" element={<WorkflowPage />} />
            <Route path="email-templates" element={<EmailTemplatesPage />} />
            <Route path="email-templates/new" element={<EmailTemplateNewPage />} />
            <Route path="email-templates/:id" element={<EmailTemplatePage />} />
          </Route>

          <Route path="analytics" element={<AnalyticsLayoutWrapper />}>
            <Route index element={<AnalyticsPage />} />
            <Route path="ecommerce" element={<AnalyticsEcommercePage />} />
            <Route path="social" element={<AnalyticsSocialPage />} />
            <Route path="delivery" element={<AnalyticsDeliveryPage />} />
          </Route>

          <Route path="automation" element={<Outlet />}>
            <Route path="email-templates" element={<Navigate to="/settings/email-templates" replace />} />
            <Route path="email-templates/new" element={<Navigate to="/settings/email-templates/new" replace />} />
            <Route path="email-templates/:id" element={<EmailTemplateNavigate />} />
            <Route path="workflows" element={<Navigate to="/settings/workflows" replace />} />
            <Route path="workflows/new" element={<Navigate to="/settings/workflows/new" replace />} />
            <Route path="workflows/:id" element={<WorkflowNavigate />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
